import { create } from 'zustand'
import { useAppearanceStore } from './appearance'

// Real-auth token store (cutover). Holds the JWT access + refresh token issued
// by the backend /auth/jwt/login. "Keep me signed in" controls persistence:
// checked → localStorage (survives browser close); unchecked → sessionStorage
// (cleared when the browser/tab closes). In maket/simulation mode these stay
// null (role-switch drives the UI) — the token is only set after a real login.

const ACCESS_KEY = 'pexify_access_token'
const REFRESH_KEY = 'pexify_refresh_token'

function safeStorage(persist: boolean): Storage | null {
  try {
    return persist ? localStorage : sessionStorage
  } catch {
    return null
  }
}

function readStored(key: string): string | null {
  // Persistent login wins; fall back to a session-only login on the same tab.
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key) ?? null
  } catch {
    return null
  }
}

// True unless a token currently lives ONLY in sessionStorage (a session login).
// Used so a token refresh keeps the user's original persistence choice.
function currentPersist(): boolean {
  try {
    if (sessionStorage.getItem(REFRESH_KEY) != null && localStorage.getItem(REFRESH_KEY) == null) {
      return false
    }
  } catch {
    // ignore
  }
  return true
}

function writeTokens(access: string, refresh: string, persist: boolean): void {
  const target = safeStorage(persist)
  const other = safeStorage(!persist)
  try {
    target?.setItem(ACCESS_KEY, access)
    target?.setItem(REFRESH_KEY, refresh)
    other?.removeItem(ACCESS_KEY)
    other?.removeItem(REFRESH_KEY)
  } catch {
    // private mode / unavailable storage — non-fatal.
  }
}

function clearStored(): void {
  try {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    sessionStorage.removeItem(ACCESS_KEY)
    sessionStorage.removeItem(REFRESH_KEY)
    sessionStorage.removeItem('pexify:catalog-studio-recipe:pending-create')
    sessionStorage.removeItem('pexify:catalog-studio-recipe:pending-repair')
    // Topbar search "Recently opened" lists (components/global-search/recent.ts)
    // hold order / customer names — never leave them for the next account.
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i)
      if (key?.startsWith('pexify.globalSearch.recent.')) localStorage.removeItem(key)
    }
  } catch {
    // ignore
  }
}

type AuthStore = {
  accessToken: string | null
  refreshToken: string | null
  // persist defaults to the current storage choice (true on first login) so a
  // token refresh in lib/api keeps the user's "keep me signed in" preference.
  setTokens: (access: string, refresh: string, persist?: boolean) => void
  clearTokens: () => void
}

export const useAuthStore = create<AuthStore>((set) => ({
  accessToken: readStored(ACCESS_KEY),
  refreshToken: readStored(REFRESH_KEY),
  setTokens: (access, refresh, persist) => {
    writeTokens(access, refresh, persist ?? currentPersist())
    set({ accessToken: access, refreshToken: refresh })
  },
  clearTokens: () => {
    clearStored()
    useAppearanceStore.getState().clearAccountAppearance()
    set({ accessToken: null, refreshToken: null })
  },
}))
